import React from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { styled } from "@mui/system";
import { RADII, COLORS, FONT_FAMILIES, FONT_WEIGHTS, FONTS, SIZES, SPACINGS } from "../../common/consts";

const TextFieldContainer = styled("div")<{ fullWidth?: boolean }>`
  display: ${({ fullWidth }) => (fullWidth ? "block" : "inline-block")};
  width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
  position: relative;

  && .MuiFormControl-root {
    width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")} !important;
    min-width: ${({ fullWidth }) => (fullWidth ? "100%" : "0")} !important;
    max-width: ${({ fullWidth }) => (fullWidth ? "100%" : "none")} !important;
    display: ${({ fullWidth }) => (fullWidth ? "block" : "inline-block")} !important;
  }

  && .MuiTextField-root {
    width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")} !important;
    min-width: ${({ fullWidth }) => (fullWidth ? "100%" : "0")} !important;
    max-width: ${({ fullWidth }) => (fullWidth ? "100%" : "none")} !important;
  }

  && .MuiInputBase-root {
    border-radius: ${RADII.md};
    background-color: var(--color-backgroundWhite, ${COLORS.backgroundWhite}) !important;
    color: var(--color-foregroundTertiary, ${COLORS.foregroundTertiary}) !important;
    width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")} !important;
    min-width: ${({ fullWidth }) => (fullWidth ? "100%" : "0")} !important;
    max-width: ${({ fullWidth }) => (fullWidth ? "100%" : "none")} !important;
    padding-left: 0 !important;

    &:hover {
      .MuiOutlinedInput-notchedOutline {
        border-color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
        border-width: 1px;
      }
    }

    &.Mui-focused {
      .MuiOutlinedInput-notchedOutline {
        border-color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
        border-width: 1px;
      }
    }
    
    .MuiOutlinedInput-notchedOutline {
      border-width: 1px;
      /* Same token the Select uses, so text fields and dropdowns sitting next
         to each other draw the same border. */
      border-color: var(--color-inputOutline, ${COLORS.inputOutline});
    }

    &:focus,
    &:focus-visible,
    &.Mui-focusVisible {
      outline: none !important;
      box-shadow: none !important;
    }

    .MuiInputBase-input:focus,
    .MuiInputBase-input:focus-visible {
      outline: none !important;
      box-shadow: none !important;
      background-color: transparent !important;
    }
  }

  && .MuiOutlinedInput-root {
    padding-left: 0 !important;
  }

  && .MuiFormLabel-root {
    color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary}) !important;
    font-family: ${FONT_FAMILIES.regular} !important;
    font-weight: ${FONT_WEIGHTS.light} !important;
    font-size: ${SIZES.inputLabelFontSize} !important;
  }

  && .MuiFormLabel-root.Mui-focused,
  && .MuiInputBase-root.Mui-focused ~ .MuiFormLabel-root,
  && .MuiInputBase-root.Mui-focused + .MuiFormLabel-root {
    color: var(--color-inputLabel, ${COLORS.inputLabel}) !important;
  }
  
  &&:has(.MuiInputBase-root.Mui-focused) .MuiFormLabel-root {
    color: var(--color-inputLabel, ${COLORS.inputLabel}) !important;
  }

  && .MuiInputBase-input {
    font-family: ${FONT_FAMILIES.regular};
    font-size: ${SIZES.inputFieldFontSize};
    font-weight: ${SIZES.inputFieldFontWeight};
    color: var(--color-foregroundTertiary, ${COLORS.foregroundTertiary}) !important;
    border: 0px;
    box-sizing: border-box;
    background: none;
    margin: ${SPACINGS.none};
    -webkit-tap-highlight-color: transparent;
    display: block;
    width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")} !important;
    min-width: ${({ fullWidth }) => (fullWidth ? "0" : "0")} !important;
    max-width: ${({ fullWidth }) => (fullWidth ? "100%" : "none")} !important;
    animation-name: mui-auto-fill-cancel;
    animation-duration: 10ms;
    height: auto;
    padding: 8px 16px 8px 16px;
  }
  
  && .MuiOutlinedInput-notchedOutline {
    min-width: ${({ fullWidth }) => (fullWidth ? "100%" : "0")} !important;
  }

  && .MuiInputAdornment-root {
    padding-left: 8px !important;
    padding-right: 0 !important;
    margin: 0 !important;
  }

  && .MuiInputAdornment-positionStart {
    padding-left: 8px !important;
    padding-right: 0 !important;
    margin-right: 0 !important;
    margin-left: 0 !important;
  }

  && .MuiOutlinedInput-root .MuiInputAdornment-positionStart {
    padding-left: 8px !important;
    padding-right: 0 !important;
  }
  
  && .MuiFormLabel-root {
    white-space: nowrap;
  }
`;

const CustomInputLabel = styled("label")<{ focused?: boolean; hasValue?: boolean }>`
  position: absolute;
  left: 14px;
  top: ${({ focused, hasValue }) => (focused || hasValue ? "0px" : "50%")};
  transform: ${({ focused, hasValue }) => (focused || hasValue ? "translateY(-50%) scale(0.75)" : "translateY(-50%) scale(1)")};
  transform-origin: top left;
  transition: color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms, transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms, max-width 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
  pointer-events: none;
  color: ${({ focused }) => (focused ? `var(--color-inputLabel, ${COLORS.inputLabel})` : `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`)};
  font-family: ${FONT_FAMILIES.regular};
  font-weight: ${FONT_WEIGHTS.light};
  font-size: ${SIZES.inputLabelFontSize};
  line-height: 1.4375em;
  z-index: 1;
  white-space: nowrap;
  background-color: var(--color-backgroundWhite, ${COLORS.backgroundWhite});
  padding: 0 4px;
  max-width: calc(133% - 32px);
`;

const CustomInputWrapper = styled("div")<{ fullWidth?: boolean; width?: number }>`
  display: ${({ fullWidth }) => (fullWidth ? "block" : "inline-block")};
  position: relative;
  width: ${({ fullWidth, width }) => (fullWidth ? "100%" : width ? `${width}px` : "auto")};
  min-width: ${({ fullWidth }) => (fullWidth ? "100%" : "fit-content")};
  
  input:focus + label {
    color: var(--color-inputLabel, ${COLORS.inputLabel}) !important;
    font-size: 0.75rem !important;
    top: 8px !important;
    transform: translateY(0) scale(0.75) !important;
  }
`;

const CustomInputField = styled("input")<{ fullWidth?: boolean; hasError?: boolean }>`
  width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
  min-width: ${({ fullWidth }) => (fullWidth ? "100%" : "fit-content")};
  padding: 16.5px 14px;
  border: 1px solid ${({ hasError }) => (hasError ? COLORS.chartEmailInvalid : `var(--color-borderTertiary, ${COLORS.borderTertiary})`)};
  border-radius: ${RADII.md};
  background-color: var(--color-backgroundWhite, ${COLORS.backgroundWhite});
  color: var(--color-foregroundTertiary, ${COLORS.foregroundTertiary});
  font-family: ${FONT_FAMILIES.regular};
  font-size: ${SIZES.inputFieldFontSize};
  font-weight: ${SIZES.inputFieldFontWeight};
  outline: none;
  box-sizing: border-box;
  min-height: 40px;
  line-height: 1.4375em;
  
  &:hover {
    border-color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
    border-width: 1px;
  }

  &:focus {
    border-color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
    border-width: 1px;
  }
  
  &:disabled {
    background-color: var(--color-backgroundSecondary, ${COLORS.backgroundSecondary});
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const HelperText = styled("p")`
  margin: 4px 0 0 0;
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.75rem;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
`;

const Input: React.ForwardRefRenderFunction<HTMLDivElement, TextFieldProps> = (
  { fullWidth = true, ...props },
  ref
) => {
  return (
    <>
      <TextFieldContainer fullWidth={fullWidth}>
        <TextField size="small" ref={ref} fullWidth={fullWidth} {...props} />
      </TextFieldContainer>
    </>
  );
};

export default React.forwardRef(Input);
